import { User, Users, Target, Dumbbell } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { services } from "../data/mock";

const Services = () => {
  const iconMap = {
    User: User,
    Users: Users,
    Target: Target,
    Dumbbell: Dumbbell,
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxwZXJzb25hbCUyMHRyYWluaW5nfGVufDB8fHx8MTc3MjAwMzE2Mnww&ixlib=rb-4.1.0&q=85"
            alt="Training Services"
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-gray-300">
              Personalized training programs designed to help you achieve your fitness goals
            </p>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What to Expect at GetFit
            </h2>
            <p className="text-lg text-gray-600">
              Every program at GetFit is tailored to your individual needs, fitness level, and
              goals. Here's what you can expect when you join our community:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            <Card className="border-2 hover:border-[#9FFF00] transition-all">
              <CardContent className="p-8">
                <div className="text-black font-bold text-4xl mb-2">01</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Initial Assessment</h3>
                <p className="text-gray-600">
                  We start with a comprehensive fitness assessment to understand your current
                  level, goals, and any limitations. This helps us create the perfect program for
                  you.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#9FFF00] transition-all">
              <CardContent className="p-8">
                <div className="text-black font-bold text-4xl mb-2">02</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Customized Plan</h3>
                <p className="text-gray-600">
                  Your trainer designs a personalized workout and nutrition plan that aligns with
                  your goals, whether it's weight loss, muscle gain, or overall fitness.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#9FFF00] transition-all">
              <CardContent className="p-8">
                <div className="text-black font-bold text-4xl mb-2">03</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Expert Guidance</h3>
                <p className="text-gray-600">
                  Train with certified professionals who provide proper form instruction, safety
                  guidance, and motivation to push you beyond your limits safely.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#9FFF00] transition-all">
              <CardContent className="p-8">
                <div className="text-black font-bold text-4xl mb-2">04</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
                <p className="text-gray-600">
                  Regular progress tracking, program adjustments, and continuous support ensure you
                  stay on track and achieve sustainable, long-term results.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Path to Fitness
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Select the training style that fits your preferences and goals
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="overflow-hidden border-2 hover:border-[#9FFF00] transition-all hover:shadow-2xl group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-[#9FFF00] rounded-full flex items-center justify-center">
                        <Icon className="w-6 h-6 text-black" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                    
                    {/* Show class types for Group Training */}
                    {service.classes && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h4 className="font-bold text-lg text-gray-900 mb-3">Class Types:</h4>
                        <ul className="space-y-3">
                          {service.classes.map((classType, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-[#9FFF00] rounded-full mt-2 flex-shrink-0"></div>
                              <div>
                                <span className="font-semibold text-gray-900">{classType.name}</span>
                                <p className="text-sm text-gray-600">{classType.description}</p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              We offer flexible packages tailored to your needs and schedule. Book a free
              consultation to discuss your goals and find the perfect training plan for you.
            </p>
            <a
              href="/contact"
              className="inline-block bg-[#9FFF00] text-black hover:bg-[#8AE600] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors"
            >
              Book Free Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
